import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileCheck2, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { BAAAuditTrail } from "@/components/BAAAuditTrail";
import { Separator } from "@/components/ui/separator";

interface SignatureRow {
  id: string;
  signer_name: string;
  signer_title: string | null;
  document_version: string;
  signed_pdf_path: string | null;
  signed_at: string;
}

/**
 * Settings card showing the user's BAA signature status, with download for the
 * signed PDF copy. Read-only — re-signing is handled by an explicit action that
 * routes back through the onboarding step.
 */
export function LegalDocumentsCard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [latest, setLatest] = useState<SignatureRow | null>(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("baa_signatures")
        .select("id,signer_name,signer_title,document_version,signed_pdf_path,signed_at")
        .eq("user_id", user.id)
        .order("signed_at", { ascending: false })
        .limit(1);
      if (cancelled) return;
      setLatest((data?.[0] as SignatureRow) ?? null);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user]);

  async function handleDownload() {
    if (!latest?.signed_pdf_path) return;
    setDownloading(true);
    try {
      const { data, error } = await supabase.storage
        .from("baa-documents")
        .createSignedUrl(latest.signed_pdf_path, 60);
      if (error || !data?.signedUrl) throw error || new Error("No URL");
      window.open(data.signedUrl, "_blank", "noopener,noreferrer");
    } catch (e: any) {
      toast.error(e?.message || "Could not download signed BAA.");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileCheck2 className="h-5 w-5 text-primary" />
          Legal Documents
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : latest ? (
          <div className="space-y-3">
            <div>
              <p className="text-sm font-semibold text-foreground">Business Associate Agreement</p>
              <p className="text-xs text-muted-foreground">
                Signed {new Date(latest.signed_at).toLocaleString()} by{" "}
                <span className="font-medium text-foreground">{latest.signer_name}</span>
                {latest.signer_title ? ` (${latest.signer_title})` : ""} · v{latest.document_version}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownload}
                disabled={!latest.signed_pdf_path || downloading}
              >
                {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Download signed copy
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => toast.info("To sign a new BAA, contact compliance@heassociatesllc.com.")}
              >
                Request new BAA
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              You have not signed a Business Associate Agreement yet. Complete onboarding to sign electronically.
            </p>
          </div>
        )}
      </CardContent>
      <CardContent className="space-y-3 pt-0">
        <Separator />
        <div>
          <p className="text-sm font-semibold text-foreground">Signing audit trail</p>
          <p className="text-xs text-muted-foreground">
            Every signing attempt — successful or failed — with signer, timestamp, device, and browser.
          </p>
        </div>
        <BAAAuditTrail />
      </CardContent>
    </Card>
  );
}